/**
 * Article enhancements — Artemis Therapeutics
 *
 * 1. Table of Contents (inline card + sticky sidebar)
 * 2. Scroll-spy for TOC highlighting
 * 3. Heading anchor links (# on hover, click to copy)
 * 4. Reading progress bar
 * 5. Back-to-top button
 * 6. Responsive table scroll hints
 * 7. Reference count badge
 * 8. Reading time remaining (desktop sidebar)
 */
document.addEventListener('DOMContentLoaded', function () {
  var content = document.querySelector('.article-template__content');

  // ── Helpers (defined before first use) ────────────────────────────────

  function scrollToHeading(id) {
    var el = document.getElementById(id);
    if (!el) return;
    var top = el.getBoundingClientRect().top + window.pageYOffset - 80;
    window.scrollTo({ top: top, behavior: 'smooth' });
    history.replaceState(null, null, '#' + id);
  }

  function makeTocLink(id, text, className) {
    var a = document.createElement('a');
    a.href = '#' + id;
    a.className = className;
    a.textContent = text;
    a.setAttribute('data-target', id);
    a.addEventListener('click', function (e) {
      e.preventDefault();
      scrollToHeading(id);
      var target = document.getElementById(id);
      if (target) {
        if (!target.getAttribute('tabindex')) {
          target.setAttribute('tabindex', '-1');
        }
        setTimeout(function () { target.focus({ preventScroll: true }); }, 400);
        target.classList.add('article-template__heading--highlight');
        setTimeout(function () {
          target.classList.remove('article-template__heading--highlight');
        }, 1800);
      }
    });
    return a;
  }

  // ── 1–2. Table of Contents + Scroll spy ─────────────────────────────────

  var inlineContainer = document.querySelector('.article-toc');
  var allLinks = null;
  var headingData = null;

  if (content) {
    var headings = content.querySelectorAll('h2, h3');

    if (headings.length >= 3) {
      headingData = [];
      headings.forEach(function (h, i) {
        if (!h.id) h.id = 'section-' + (i + 1);
        headingData.push({ id: h.id, text: h.textContent, tag: h.tagName, el: h });
      });

      // Inline TOC (mobile card)
      if (inlineContainer) {
        var list = document.createElement('ol');
        list.className = 'article-toc__list';
        var currentH2Li = null;
        var currentSubList = null;

        headingData.forEach(function (h) {
          var li = document.createElement('li');
          var link = makeTocLink(h.id, h.text, 'article-toc__link');
          li.appendChild(link);

          if (h.tag === 'H2') {
            li.className = 'article-toc__item';
            currentSubList = null;
            currentH2Li = li;
            list.appendChild(li);
          } else {
            li.className = 'article-toc__item article-toc__item--sub';
            if (currentH2Li) {
              if (!currentSubList) {
                currentSubList = document.createElement('ol');
                currentSubList.className = 'article-toc__sublist';
                currentH2Li.appendChild(currentSubList);
              }
              currentSubList.appendChild(li);
            } else {
              list.appendChild(li);
            }
          }
        });

        inlineContainer.appendChild(list);
        inlineContainer.style.display = 'block';
      }

      // Sidebar TOC (desktop sticky)
      var sidebar = document.createElement('aside');
      sidebar.className = 'article-toc-sidebar';
      sidebar.setAttribute('aria-label', 'Table of contents');

      var nav = document.createElement('nav');
      nav.className = 'article-toc-sidebar__nav';
      nav.setAttribute('aria-label', 'Table of contents');

      var tocHeading = document.createElement('p');
      tocHeading.className = 'article-toc-sidebar__heading';
      tocHeading.textContent = 'On this page';
      nav.appendChild(tocHeading);

      var sidebarList = document.createElement('ul');
      sidebarList.className = 'article-toc-sidebar__list';

      headingData.forEach(function (h) {
        var li = document.createElement('li');
        li.className = 'article-toc-sidebar__item';
        var link = makeTocLink(h.id, h.text, 'article-toc-sidebar__link');
        if (h.tag === 'H3') link.classList.add('article-toc-sidebar__link--sub');
        li.appendChild(link);
        sidebarList.appendChild(li);
      });

      nav.appendChild(sidebarList);
      sidebar.appendChild(nav);
      content.appendChild(sidebar);

      allLinks = document.querySelectorAll('.article-toc__link, .article-toc-sidebar__link');
    }
  }

  // ── 3. Heading anchor links ─────────────────────────────────────────────

  if (content) {
    content.querySelectorAll('h2, h3').forEach(function (h) {
      if (!h.id) return;
      var anchor = document.createElement('a');
      anchor.href = '#' + h.id;
      anchor.className = 'article-heading-anchor';
      anchor.setAttribute('aria-label', 'Link to this section');
      anchor.textContent = '#';
      h.style.position = 'relative';
      h.appendChild(anchor);

      anchor.addEventListener('click', function (e) {
        e.preventDefault();
        var url = window.location.href.split('#')[0] + '#' + h.id;
        if (navigator.clipboard) {
          navigator.clipboard.writeText(url);
        }
        anchor.textContent = '\u2713';
        anchor.classList.add('article-heading-anchor--copied');
        setTimeout(function () {
          anchor.textContent = '#';
          anchor.classList.remove('article-heading-anchor--copied');
        }, 1500);
        scrollToHeading(h.id);
      });
    });
  }

  // ── 4–5. Unified scroll handler (scroll-spy + progress bar + back-to-top)

  var progressBar = document.querySelector('.article-progress-bar');
  var backToTop = document.querySelector('.article-back-to-top');
  var progressContent = (progressBar) ? (content || document.querySelector('.article-template')) : null;
  var scrollMilestones = [25, 50, 75, 100];
  var firedMilestones = {};

  // Reading time remaining — vars declared here so onScroll can access them
  var timeRemainingEl = null;
  var totalReadMinutes = 0;

  var needsScroll = allLinks || progressContent || backToTop;
  if (needsScroll) {
    var scrollTicking = false;

    function onScroll() {
      var scrollPos = window.pageYOffset;

      // Scroll spy
      if (allLinks && headingData) {
        var spyPos = scrollPos + 120;
        var activeId = null;
        for (var i = headingData.length - 1; i >= 0; i--) {
          if (headingData[i].el.offsetTop <= spyPos) {
            activeId = headingData[i].id;
            break;
          }
        }
        allLinks.forEach(function (link) {
          var target = link.getAttribute('data-target');
          if (target === activeId) {
            link.classList.add('article-toc__link--active');
            link.classList.add('article-toc-sidebar__link--active');
            link.setAttribute('aria-current', 'location');
          } else {
            link.classList.remove('article-toc__link--active');
            link.classList.remove('article-toc-sidebar__link--active');
            link.removeAttribute('aria-current');
          }
        });
      }

      // Progress percentage (shared by progress bar + reading time remaining)
      var pct = 0;
      if (progressContent) {
        var rect = progressContent.getBoundingClientRect();
        var top = rect.top + scrollPos;
        var bottom = top + rect.height;
        pct = (scrollPos + window.innerHeight * 0.4 - top) / (bottom - top);
        pct = Math.min(Math.max(pct, 0), 1);
        progressBar.style.width = (pct * 100).toFixed(1) + '%';

        if (window.posthog) {
          var pctInt = Math.floor(pct * 100);
          for (var m = 0; m < scrollMilestones.length; m++) {
            var milestone = scrollMilestones[m];
            if (pctInt >= milestone && !firedMilestones[milestone]) {
              firedMilestones[milestone] = true;
              posthog.capture('article_scroll_depth', { depth_percent: milestone });
            }
          }
        }
      }

      // Back to top
      if (backToTop) {
        if (scrollPos > 500) {
          backToTop.classList.add('article-back-to-top--visible');
        } else {
          backToTop.classList.remove('article-back-to-top--visible');
        }
      }

      // Reading time remaining
      if (timeRemainingEl && progressContent) {
        var remaining = Math.max(0, Math.round(totalReadMinutes * (1 - pct)));
        timeRemainingEl.textContent = remaining <= 0 ? 'Done reading' : '~' + remaining + ' min left';
      }

      scrollTicking = false;
    }

    window.addEventListener('scroll', function () {
      if (!scrollTicking) {
        window.requestAnimationFrame(onScroll);
        scrollTicking = true;
      }
    }, { passive: true });

    onScroll();
  }

  if (backToTop) {
    backToTop.addEventListener('click', function () {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  // ── 6. Responsive table scroll hints ────────────────────────────────────

  var wrappers = document.querySelectorAll('.table-scroll');
  wrappers.forEach(function (wrapper) {
    function check() {
      if (wrapper.scrollWidth <= wrapper.clientWidth + 1) {
        wrapper.classList.add('table-scroll--fits');
      } else {
        wrapper.classList.remove('table-scroll--fits');
        var atEnd = wrapper.scrollLeft + wrapper.clientWidth >= wrapper.scrollWidth - 4;
        wrapper.classList.toggle('table-scroll--scrolled-end', atEnd);
      }
    }
    wrapper.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check, { passive: true });
    check();
  });

  // ── 7. Glossary tooltip hydration ───────────────────────────────────────

  if (content) {
    var glossaryEl = document.getElementById('article-glossary-data');
    if (glossaryEl) {
      try {
        var glossary = JSON.parse(glossaryEl.textContent);
        content.querySelectorAll('dfn').forEach(function (dfn) {
          var term = dfn.textContent.trim().toLowerCase();
          if (glossary[term] && !dfn.getAttribute('data-definition')) {
            dfn.setAttribute('data-definition', glossary[term]);
            dfn.setAttribute('tabindex', '0');
          }
        });
      } catch (e) { /* silently skip if JSON is malformed */ }
    }

    // ── 7b. Glossary tap-to-reveal (mobile) ──────────────────────────────
    if ('ontouchstart' in window) {
      content.querySelectorAll('dfn[data-definition]').forEach(function (dfn) {
        dfn.addEventListener('click', function (e) {
          e.stopPropagation();
          var wasActive = dfn.classList.contains('tooltip-active');
          content.querySelectorAll('dfn.tooltip-active').forEach(function (d) {
            d.classList.remove('tooltip-active');
          });
          if (!wasActive) dfn.classList.add('tooltip-active');
        });
      });
      document.addEventListener('click', function () {
        content.querySelectorAll('dfn.tooltip-active').forEach(function (d) {
          d.classList.remove('tooltip-active');
        });
      });
    }
  }

  // ── 8. Reference count badge ──────────────────────────────────────────

  if (content) {
    var citationLinks = content.querySelectorAll('sup a[href^="#"]');
    var refCount = citationLinks.length;
    if (refCount > 0) {
      var metaBar = document.querySelector('.article-template__meta');
      if (metaBar) {
        var badge = document.createElement('span');
        badge.className = 'article-template__ref-count';
        badge.textContent = refCount + ' reference' + (refCount !== 1 ? 's' : '') + ' cited';
        metaBar.appendChild(badge);
      }
    }
  }

  // ── 9. Reading time remaining (desktop sidebar) ───────────────────────
  //    timeRemainingEl + totalReadMinutes are declared above onScroll
  //    so the scroll handler can access them directly.

  if (content && headingData && window.innerWidth >= 990) {
    totalReadMinutes = Math.max(1, Math.round(content.textContent.split(/\s+/).length / 230));

    var tocHeadingEl = document.querySelector('.article-toc-sidebar__heading');
    if (tocHeadingEl) {
      timeRemainingEl = document.createElement('span');
      timeRemainingEl.className = 'article-toc-sidebar__time-remaining';
      timeRemainingEl.textContent = '~' + totalReadMinutes + ' min left';
      tocHeadingEl.parentNode.insertBefore(timeRemainingEl, tocHeadingEl.nextSibling);
    }
  }
});
